import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Table,
    Form,
    InputGroup,
    Spinner,
    Row,
    Col,
} from 'react-bootstrap';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';

function UserOrderHistoryPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchOrders() {
            setLoading(true);
            try {
                const res = await fetch('/api/orders/my-orders/', {
                    credentials: 'include',
                });
                if (!res.ok) throw new Error(res.statusText);

                const data = await res.json();
                setOrders(data);
            } catch (err) {
                console.error('Error fetching orders:', err);
                setOrders([]);
            } finally {
                setLoading(false);
            }
        }
        fetchOrders();
    }, []);

    // define your columns once
    const columns = useMemo(
        () => [
            {
                header: 'Order #',
                accessorKey: 'order_number',
                cell: info => `#${info.getValue()}`,
            },
            {
                header: 'Date',
                accessorKey: 'created_at',
                cell: info => new Date(info.getValue()).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                }),
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: info => (
                    <span className={`badge bg-${getStatusBadge(info.getValue())}`}>
                        {info.getValue().charAt(0).toUpperCase() + info.getValue().slice(1)}
                    </span>
                ),
            },
            {
                header: 'Items',
                accessorFn: row => row.items,
                cell: info => (
                    <ul className="mb-0 ps-3" style={listStyle}>
                        {info.getValue().map(item => (
                            <li key={item.id}>
                                {item.quantity}× {item.product.name} @ $
                                {parseFloat(item.price).toFixed(2)}
                            </li>
                        ))}
                    </ul>
                ),
            },
            {
                header: 'Total',
                accessorKey: 'total_amount',
                cell: info => `$${parseFloat(info.getValue()).toFixed(2)}`,
            },
        ],
        []
    );

    // create the table instance
    const table = useReactTable({
        data: Array.isArray(orders) ? orders : [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    // Add styles for the list and badges
    const listStyle = {
        listStyleType: 'none',
        paddingLeft: 0,
    };

    const getStatusBadge = (status) => {
        switch (status.toLowerCase()) {
            case 'paid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'cancelled':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    // Apply different font sizes and weights
    const headerStyle = {
        fontSize: '1.1rem',
        fontWeight: 'bold',
    };

    const cellStyle = {
        color: '#6c757d',
        fontSize: '0.9rem',
        fontWeight: 'normal',
        backgroundColor: 'white',
    };

    const orderNumberStyle = {
        color: '#6c757d',
        fontWeight: 'bold',
        fontSize: '14.4px',
        backgroundColor: 'white',
    };

    return (
        <Card className="my-4">
            <Card.Header>
                <h5 className="card-title">Order History</h5>
                <p className="mb-0">View your past orders here.</p>
            </Card.Header>
            <Card.Body>
                {loading && (
                    <div className="position-absolute top-50 start-50 translate-middle">
                        <Spinner animation="border" />
                    </div>
                )}

                <Table hover responsive className="table-custom">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        style={{ ...headerStyle, cursor: 'pointer' }}
                                    >
                                        {!header.isPlaceholder && flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id} style={cell.column.id === 'order_number' ? orderNumberStyle : cellStyle}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    );
}

export default UserOrderHistoryPage; 